import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Package,
  Search,
  RefreshCw,
  Eye,
  EyeOff,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Filter
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { formatPrice, getImageUrl } from '../../utils/helpers';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from '../../hooks/use-toast';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 20;

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const [productsData, countData] = await Promise.all([
        adminAPI.getProducts({ skip: page * limit, limit, active: activeFilter, search }),
        adminAPI.getProductsCount({ active: activeFilter, search })
      ]);
      setProducts(productsData);
      setTotalCount(countData.count);
    } catch (err) {
      console.error('Error loading products:', err);
      toast({ title: 'Error', description: 'Error al cargar productos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, activeFilter, search]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleToggleActive = async (productId) => {
    try {
      await adminAPI.toggleProductActive(productId);
      loadProducts();
      toast({ title: 'Éxito', description: 'Estado del producto actualizado' });
    } catch (err) {
      toast({ title: 'Error', description: 'Error al actualizar producto', variant: 'destructive' });
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
    
    try {
      await adminAPI.deleteProduct(productId);
      loadProducts();
      toast({ title: 'Éxito', description: 'Producto eliminado' });
    } catch (err) {
      toast({ title: 'Error', description: 'Error al eliminar producto', variant: 'destructive' });
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Productos</h1>
            <p className="text-gray-400 mt-1">{totalCount} productos en total</p>
          </div>
          <Button onClick={loadProducts} variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-gray-800 border-gray-700 p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  placeholder="Buscar productos..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  className="pl-10 bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant={activeFilter === null ? 'default' : 'outline'}
                onClick={() => { setActiveFilter(null); setPage(0); }}
                className={activeFilter === null ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-600 text-gray-300'}
              >
                Todos
              </Button>
              <Button
                variant={activeFilter === true ? 'default' : 'outline'}
                onClick={() => { setActiveFilter(true); setPage(0); }}
                className={activeFilter === true ? 'bg-green-600 hover:bg-green-700' : 'border-gray-600 text-gray-300'}
              >
                Activos
              </Button>
              <Button
                variant={activeFilter === false ? 'default' : 'outline'}
                onClick={() => { setActiveFilter(false); setPage(0); }}
                className={activeFilter === false ? 'bg-red-600 hover:bg-red-700' : 'border-gray-600 text-gray-300'}
              >
                Inactivos
              </Button>
            </div>
          </div>
        </Card>

        {/* Products table */}
        <Card className="bg-gray-800 border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No se encontraron productos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Producto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Precio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Stock</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Vendedor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {products.map((product) => (
                    <tr key={product._id} className="hover:bg-gray-700/50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={getImageUrl(product.images?.[0])}
                            alt={product.title}
                            className="w-12 h-12 rounded object-cover bg-gray-700"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/48'}
                          />
                          <div>
                            <p className="text-white font-medium truncate max-w-[200px]">{product.title}</p>
                            <p className="text-gray-400 text-sm">{product.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-white">{formatPrice(product.price)}</td>
                      <td className="px-4 py-4 text-white">{product.stock}</td>
                      <td className="px-4 py-4 text-gray-400">{product.seller}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          product.active ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                        }`}>
                          {product.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(product._id)}
                            className="text-gray-400 hover:text-white"
                            title={product.active ? 'Desactivar' : 'Activar'}
                          >
                            {product.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(product._id)}
                            className="text-gray-400 hover:text-red-400"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
              <p className="text-sm text-gray-400">
                Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, totalCount)} de {totalCount}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="border-gray-600 text-gray-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="border-gray-600 text-gray-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
